import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { obtenerClienteDetalle } from '../servicios/api';

// Componente para mostrar un campo de detalle
const DetailField = ({ label, value }) => (
    <div className="py-3 sm:grid sm:grid-cols-3 sm:gap-4">
        <dt className="text-sm font-medium text-gray-500">{label}</dt>
        <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{value || 'N/A'}</dd>
    </div>
);

// Componente para renderizar la lista de facturas
const FacturasList = ({ facturas }) => {
    if (!facturas || facturas.length === 0) {
        return <p className="text-sm text-gray-500">No hay facturas para este cliente.</p>;
    }

    return (
        <div className="overflow-x-auto">
            <table className="min-w-full bg-white border">
                <thead className="bg-gray-800 text-white">
                    <tr>
                        <th className="py-2 px-4 text-left">Nº Factura</th>
                        <th className="py-2 px-4 text-left">Fecha</th>
                        <th className="py-2 px-4 text-right">Total</th>
                        <th className="py-2 px-4 text-center">Estado</th>
                    </tr>
                </thead>
                <tbody>
                    {facturas.map((factura) => (
                        <tr key={factura.id_factura} className="border-b hover:bg-gray-50">
                            <td className="py-2 px-4">{factura.numero_factura}</td>
                            <td className="py-2 px-4">{new Date(factura.fecha_emision).toLocaleDateString()}</td>
                            <td className="py-2 px-4 text-right">{parseFloat(factura.total).toFixed(2)} €</td>
                            <td className="py-2 px-4 text-center">{factura.estado}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

// Componente para renderizar la lista de avisos
const AvisosList = ({ avisos }) => {
    if (!avisos || avisos.length === 0) {
        return <p className="text-sm text-gray-500">No hay avisos para este cliente.</p>;
    }

    return (
        <div className="overflow-x-auto">
            <table className="min-w-full bg-white border">
                <thead className="bg-gray-800 text-white">
                    <tr>
                        <th className="py-2 px-4 text-left">ID Aviso</th>
                        <th className="py-2 px-4 text-left">Descripción</th>
                        <th className="py-2 px-4 text-left">Fecha</th>
                        <th className="py-2 px-4 text-center">Estado</th>
                    </tr>
                </thead>
                <tbody>
                    {avisos.map((aviso) => (
                        <tr key={aviso.id_aviso} className="border-b hover:bg-gray-50">
                            <td className="py-2 px-4">{aviso.numero_aviso ?? aviso.id_aviso}</td>
                            <td className="py-2 px-4">{aviso.descripcion}</td>
                            <td className="py-2 px-4">{new Date(aviso.fecha_aviso).toLocaleDateString()}</td>
                            <td className="py-2 px-4 text-center">{aviso.estado}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

const VerClientePage = () => {
    const { id } = useParams(); // Obtener el ID del cliente de la URL
    const [cliente, setCliente] = useState(null);
    const [facturas, setFacturas] = useState([]); // Estado para las facturas
    const [avisos, setAvisos] = useState([]); // Estado para los avisos
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const cargarDetalles = async () => {
            try {
                setIsLoading(true);
                const respuesta = await obtenerClienteDetalle(id);
                if (respuesta.exito) {
                    setCliente(respuesta.cliente);
                    setFacturas(respuesta.facturas || []); // Guardar facturas, con fallback a array vacío
                    setAvisos(respuesta.avisos || []); // Guardar avisos, con fallback a array vacío
                } else {
                    setError(respuesta.mensaje);
                }
            } catch (err) {
                setError('No se pudo conectar con el servidor.');
                console.error(err);
            } finally {
                setIsLoading(false);
            }
        };

        cargarDetalles();
    }, [id]); // Se ejecuta cada vez que el ID de la URL cambia

    if (isLoading) {
        return <div className="text-center p-10">Cargando detalles del cliente...</div>;
    }

    if (error) {
        return (
            <div className="text-center p-10 text-red-500">
                <p>Error: {error}</p>
                <Link to="/clientes" className="mt-4 inline-block bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600">Volver a la lista</Link>
            </div>
        );
    }

    if (!cliente) {
        return <div className="text-center p-10">No se encontró el cliente.</div>;
    }

    return (
        <div className="bg-white p-5 max-w-4xl mx-auto shadow-lg rounded-lg">
            <div className="flex justify-between items-center mb-6 pb-4 border-b">
                <h2 className="text-3xl font-bold text-gray-800">Detalles del Cliente</h2>
                <Link to="/clientes" className="bg-gray-800 text-white px-5 py-2.5 rounded-md text-sm font-semibold hover:bg-gray-700 transition-colors">
                    &larr; Volver a la lista
                </Link>
            </div>

            <div className="border-t border-gray-200">
                <dl>
                    <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                        <dt className="text-sm font-bold text-gray-700">Nombre Completo</dt>
                        <dd className="mt-1 text-lg font-semibold text-gray-900 sm:mt-0 sm:col-span-2">{cliente.nombre_cliente}</dd>
                    </div>
                    <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                        <DetailField label="NIF / NIE" value={cliente.nif} />
                    </div>
                    <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                        <DetailField label="Email" value={cliente.correo_electronico} />
                    </div>
                    <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                        <DetailField label="Teléfono" value={cliente.telefono} />
                    </div>
                    <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                        <DetailField label="Dirección" value={cliente.direccion} />
                    </div>
                    <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                        <DetailField label="Población" value={cliente.poblacion} />
                    </div>
                    <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                        <DetailField label="Código Postal" value={cliente.codigo_postal} />
                    </div>
                    <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                        <DetailField label="Provincia" value={cliente.provincia} />
                    </div>
                    <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                        <DetailField label="País" value={cliente.pais} />
                    </div>
                    <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                        <DetailField label="IBAN" value={cliente.iban} />
                    </div>
                    <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                        <DetailField label="Notas" value={cliente.notas} />
                    </div>
                </dl>
            </div>

            {/* Sección de Historial de Facturas */}
            <div className="mt-10">
                <h3 className="text-2xl font-semibold text-gray-800 mb-4 border-b pb-2">Historial de Facturas</h3>
                <FacturasList facturas={facturas} />
            </div>

            {/* Sección de Historial de Avisos */}
            <div className="mt-10">
                <h3 className="text-2xl font-semibold text-gray-800 mb-4 border-b pb-2">Historial de Avisos / Reparaciones</h3>
                <AvisosList avisos={avisos} />
            </div>
        </div>
    );
};

export default VerClientePage;
